import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { SidebarSectionToggle } from "@/components/_templates/sidebar/sidebar-section-toggle";
import { useSharedEntityList } from "@/domain/team/hooks/api/use-shared-team-list";
import { useTeamListForWorkspace } from "@/domain/team/hooks/api/use-team-list-for-workspace";
import { CurrentUser } from "@/domain/user/models/current-user-model";
import { Workspace } from "@/domain/workspace/models/workspace-model";

import { buildSharedTeamNavList } from "./build-shared-team-nav-list";
import { SidebarAppNoTeamCard } from "./sidebar-app-no-team-card";
import { SidebarAppTeamNavElement } from "./sidebar-app-team-nav-element";

interface SidebarAppAllTeamsNavElementProps {
  workspace: Workspace;
  user: CurrentUser;
}

export const SidebarAppAllTeamsNavElement = ({ workspace, user }: SidebarAppAllTeamsNavElementProps) => {
  const [expanded, setExpanded] = useState(true);
  const [sharedExpanded, setSharedExpanded] = useState(true);
  const { t } = useTranslation("common");
  const { getTeamListForUser, teamList } = useTeamListForWorkspace({
    workspaceId: workspace.id,
  });
  const activeTeamListForCurrentUser = getTeamListForUser(user.id, {
    statusList: ["ACTIVE"],
  });

  const { sharedTeamGroupList } = useSharedEntityList({
    workspaceId: workspace.id,
  });

  const memberTeamIdSet = useMemo(
    () => new Set(activeTeamListForCurrentUser.map((team) => team.id)),
    [activeTeamListForCurrentUser],
  );

  const sharedTeamNavList = useMemo(
    () => buildSharedTeamNavList({ sharedTeamGroupList, teamList, memberTeamIdSet }),
    [sharedTeamGroupList, teamList, memberTeamIdSet],
  );

  const handleClick = () => {
    setExpanded(!expanded);
  };

  const handleSharedClick = () => {
    setSharedExpanded(!sharedExpanded);
  };

  return (
    <>
      <SidebarSectionToggle label={t("sidebar.myTeams")} expanded={expanded} onToggle={handleClick} />
      {expanded && (
        <>
          {activeTeamListForCurrentUser.map((team) => (
            <SidebarAppTeamNavElement key={team.id} team={team} workspace={workspace} expanded={true} />
          ))}
          {activeTeamListForCurrentUser.length === 0 && <SidebarAppNoTeamCard />}
        </>
      )}

      {sharedTeamNavList.length > 0 && (
        <>
          <SidebarSectionToggle label={t("sidebar.sharedWithMe")} expanded={sharedExpanded} onToggle={handleSharedClick} />
          {sharedExpanded &&
            sharedTeamNavList.map(({ team, entityTypeSet }) => (
              <SidebarAppTeamNavElement
                key={`shared-${team.id}`}
                team={team}
                workspace={workspace}
                isShared
                sharedEntityTypeSet={entityTypeSet}
              />
            ))}
        </>
      )}
    </>
  );
};
